import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getStatus } from "@/lib/api";
import { CheckCircle, Circle, XCircle, Spinner } from "@phosphor-icons/react";

function StageRow({ s }) {
  let icon = <Circle size={16} weight="regular" className="text-zinc-400" />;
  let labelClass = "text-zinc-500";
  let suffix = null;

  if (s.status === "running") {
    icon = <Spinner size={16} weight="bold" className="text-amber-600 animate-spin" />;
    labelClass = "text-zinc-950 font-medium cursor-blink";
  } else if (s.status === "complete") {
    icon = <CheckCircle size={16} weight="fill" className="text-emerald-600" />;
    labelClass = "text-zinc-700";
  } else if (s.status === "failed") {
    icon = <XCircle size={16} weight="fill" className="text-red-600" />;
    labelClass = "text-red-700 font-medium";
    suffix = s.note ? (
      <div className="ml-7 mt-1 text-xs text-red-700 font-mono">{s.note}</div>
    ) : null;
  }

  return (
    <div className="border-b border-zinc-200 last:border-b-0" data-testid={`stage-${s.key}`}>
      <div className="px-5 py-3 flex items-center gap-3">
        <div className="w-4 flex justify-center">{icon}</div>
        <div className={`font-mono text-sm ${labelClass}`}>{s.label}</div>
        <div className="ml-auto font-mono text-[10px] uppercase tracking-widest text-zinc-400">
          {s.status}
        </div>
      </div>
      {suffix}
    </div>
  );
}

export default function Progress() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["status", id],
    queryFn: () => getStatus(id),
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      if (s === "complete" || s === "failed") return false;
      return 2000;
    },
  });

  useEffect(() => {
    if (data?.status === "complete") {
      // Auto-route to report
      const t = setTimeout(() => navigate(`/analysis/${id}/report`), 600);
      return () => clearTimeout(t);
    }
  }, [data?.status, id, navigate]);

  if (isLoading || !data) {
    return (
      <div className="flex-1 p-12">
        <div className="font-mono text-xs text-zinc-500">Loading analysis…</div>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-screen flex flex-col" data-testid="progress-page">
      <div className="border-b border-zinc-200 px-6 lg:px-12 py-6 bg-white">
        <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-1">
          Run · {id.slice(0, 8)}
        </div>
        <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950">
          Pipeline running
        </h1>
      </div>

      <div className="flex-1 max-w-3xl w-full mx-auto px-6 lg:px-12 py-10">
        <div className="border border-zinc-200 bg-white" data-testid="pipeline-stages">
          <div className="px-5 py-3 border-b border-zinc-200 font-mono text-[10px] uppercase tracking-widest text-zinc-500 bg-zinc-50">
            $ teardown.run --target {id.slice(0, 8)}
          </div>
          {data.stages.map((s) => (
            <StageRow key={s.key} s={s} />
          ))}
        </div>

        {data.status === "failed" && (
          <div
            data-testid="pipeline-failed"
            className="mt-6 border border-red-300 bg-red-50 p-5"
          >
            <div className="font-display font-semibold text-red-700 mb-1">
              Analysis failed
            </div>
            <div className="font-mono text-sm text-red-700">
              {data.error || "Unknown error"}
            </div>
            <button
              onClick={() => navigate("/")}
              data-testid="failed-back-button"
              className="mt-4 bg-zinc-950 text-white px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-zinc-800"
            >
              Start a new run
            </button>
          </div>
        )}

        {data.status === "complete" && (
          <div className="mt-6 border border-emerald-300 bg-emerald-50 p-5 flex items-center justify-between">
            <div>
              <div className="font-display font-semibold text-emerald-800">Run complete</div>
              <div className="font-mono text-xs text-emerald-700 mt-1">Opening report…</div>
            </div>
            <button
              onClick={() => navigate(`/analysis/${id}/report`)}
              data-testid="open-report-button"
              className="bg-zinc-950 text-white px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-zinc-800"
            >
              Open report
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
